import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import './styles.css';

// ——— Types mirrored from the API (kept local: the harness UI is fully separate) ———

interface Submission {
  id: string;
  tenant_id: string;
  portco_id: string;
  portco_name: string;
  period: string;
  state: string;
  expected_items: number;
  received_items: number;
  missing_items: number;
  workflow_run_id: string | null;
  updated_at: string;
  last_transition?: Transition | null;
}

interface Transition {
  seq: number;
  from_state: string;
  event: string;
  to_state: string;
  action: string | null;
  payload: Record<string, unknown> | null;
  actor: string;
  idempotency_key: string | null;
  created_at: string;
}

interface HarnessEvent {
  id: number;
  at: string;
  kind: string;
  direction: 'in' | 'out' | 'local';
  summary: string;
  detail: unknown;
}

interface HarnessConfig {
  manifest: {
    configured: boolean;
    base_url: string | null;
    start_run_path: string;
    signal_path: string;
  };
  interface_b: {
    token_is_default: boolean;
    token: string;
    transitions_route: string;
    read_route: string;
    webhook_route: string;
  };
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: init?.body ? { 'content-type': 'application/json' } : undefined,
  });
  const body = (await res.json().catch(() => null)) as (T & { error?: string }) | null;
  if (!res.ok) throw new Error(body?.error ?? `${res.status} ${res.statusText}`);
  return body as T;
}

function timeOf(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour12: false });
}

function StateBadge({ state }: { state: string }) {
  return <span className={`state state-${state}`}>{state}</span>;
}

// ——— Create form ———

function CreateForm({ onCreated }: { onCreated: () => void }) {
  const [tenant, setTenant] = useState('t1');
  const [portcoId, setPortcoId] = useState('');
  const [portcoName, setPortcoName] = useState('');
  const [period, setPeriod] = useState('2026-07');
  const [expected, setExpected] = useState(4);
  const [contacts, setContacts] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/api/submissions', {
        method: 'POST',
        body: JSON.stringify({
          tenant_id: tenant,
          portco_id: portcoId || portcoName.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          portco_name: portcoName,
          period,
          expected_items: expected,
          contact_emails: contacts
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
        }),
      });
      setPortcoId('');
      setPortcoName('');
      onCreated();
    } catch (err) {
      setError(String(err instanceof Error ? err.message : err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="create" onSubmit={submit}>
      <div className="row">
        <input value={tenant} onChange={(e) => setTenant(e.target.value)} placeholder="tenant id" />
        <input
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          placeholder="period (YYYY-MM)"
        />
      </div>
      <input
        value={portcoName}
        onChange={(e) => setPortcoName(e.target.value)}
        placeholder="portco name (e.g. Acme Robotics)"
        required
      />
      <div className="row">
        <input
          value={portcoId}
          onChange={(e) => setPortcoId(e.target.value)}
          placeholder="portco id (optional)"
        />
        <input
          type="number"
          min={1}
          value={expected}
          onChange={(e) => setExpected(Number(e.target.value))}
          title="expected items"
        />
      </div>
      <input
        value={contacts}
        onChange={(e) => setContacts(e.target.value)}
        placeholder="contact emails, comma-separated"
      />
      {error && <div className="error-banner">{error}</div>}
      <button className="primary" disabled={busy || !portcoName}>
        Create submission
      </button>
    </form>
  );
}

// ——— Detail panel ———

function Detail({
  submission,
  config,
  refresh,
}: {
  submission: Submission;
  config: HarnessConfig | null;
  refresh: () => void;
}) {
  const [transitions, setTransitions] = useState<Transition[]>([]);
  const [received, setReceived] = useState(0);
  const [missing, setMissing] = useState(submission.expected_items);
  const [resumeState, setResumeState] = useState('request_sent');
  const [error, setError] = useState<string | null>(null);
  const [audit, setAudit] = useState<string | null>(null);

  const load = useCallback(async () => {
    const data = await api<{ transitions: Transition[] }>(`/api/submissions/${submission.id}`);
    setTransitions(data.transitions);
  }, [submission.id]);

  useEffect(() => {
    void load();
  }, [load, submission.state, submission.updated_at]);

  const act = async (path: string, body?: unknown) => {
    setError(null);
    try {
      await api(`/api/submissions/${submission.id}/${path}`, {
        method: 'POST',
        body: JSON.stringify(body ?? {}),
      });
    } catch (err) {
      setError(String(err instanceof Error ? err.message : err));
    } finally {
      refresh();
      void load();
    }
  };

  const verifyAudit = async () => {
    const result = await api<{ ok: boolean; length: number; brokenAtIndex?: number }>(
      '/api/audit/verify',
    );
    setAudit(
      result.ok
        ? `chain OK (${result.length} entries)`
        : `chain BROKEN at index ${result.brokenAtIndex}`,
    );
  };

  const curl = useMemo(() => {
    if (!config) return '';
    const token = config.interface_b.token;
    return [
      `# Interface B — record a transition (what the Manifest workflow calls)`,
      `curl -s -X POST '${location.origin}/internal/submissions/${submission.id}/transitions' \\`,
      `  -H 'Authorization: Bearer ${token}' -H 'Content-Type: application/json' \\`,
      `  -d '{`,
      `    "event": "request_dispatched",`,
      `    "to_state": "request_sent",`,
      `    "action": "request_email_sent",`,
      `    "payload": {"message_id": "msg-001", "recipient_person_ids": ["p1"]},`,
      `    "actor": "agent:monitor-agent@v3",`,
      `    "workflow_run_id": "${submission.workflow_run_id ?? 'run-id'}",`,
      `    "idempotency_key": "${submission.workflow_run_id ?? 'run-id'}:${transitions.length}"`,
      `  }'`,
      ``,
      `# Interface B — state read (crash recovery / reconcile after a 409)`,
      `curl -s '${location.origin}/internal/submissions/${submission.id}' \\`,
      `  -H 'Authorization: Bearer ${token}'`,
      ``,
      `# Failure webhook`,
      `curl -s -X POST '${location.origin}/webhooks/manifest' \\`,
      `  -H 'Authorization: Bearer ${token}' -H 'Content-Type: application/json' \\`,
      `  -d '{"type": "run.failed", "run_id": "${submission.workflow_run_id ?? 'run-id'}", "reason": "example"}'`,
    ].join('\n');
  }, [config, submission, transitions.length]);

  return (
    <>
      <div className="detail-head">
        <span className="name">{submission.portco_name}</span>
        <StateBadge state={submission.state} />
        <span className="kv">{submission.period}</span>
        <span className="kv">
          items {submission.received_items}/{submission.expected_items} (missing{' '}
          {submission.missing_items})
        </span>
        <span className="kv">run: {submission.workflow_run_id ?? '—'}</span>
        <span className="kv">id: {submission.id}</span>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="actions">
        <button
          className="primary"
          disabled={submission.state !== 'not_started'}
          onClick={() => act('start', config?.manifest.configured ? {} : { offline: true })}
          title={
            config?.manifest.configured
              ? 'Interface A: start the Manifest workflow run'
              : 'Manifest not configured — records period_opened locally so Interface B can be tested with curl'
          }
        >
          {config?.manifest.configured ? '▶ Start run (A)' : '▶ Start run (offline)'}
        </button>

        <div className="group" title="Interface C: simulate intake detecting documents">
          <label>items_received</label>
          <input
            className="num"
            type="number"
            min={0}
            value={received}
            onChange={(e) => setReceived(Number(e.target.value))}
          />
          <label>recv /</label>
          <input
            className="num"
            type="number"
            min={0}
            value={missing}
            onChange={(e) => setMissing(Number(e.target.value))}
          />
          <label>missing</label>
          <button
            onClick={() =>
              act('signals', {
                name: 'items_received',
                payload: { received, missing, doc_ids: [] },
              })
            }
          >
            Signal (C)
          </button>
        </div>

        <div className="group" title="Interface C: simulate the trust-layer pipeline finishing">
          <label>pipeline</label>
          <button
            onClick={() =>
              act('signals', { name: 'pipeline_completed', payload: { extraction_run_id: 'xr-demo' } })
            }
          >
            completed
          </button>
          <button onClick={() => act('signals', { name: 'pipeline_failed', payload: { error: 'demo failure' } })}>
            failed
          </button>
        </div>

        <div className="group" title="User-actored (§5.4): commits locally first, then signals">
          <label>resolve →</label>
          <select value={resumeState} onChange={(e) => setResumeState(e.target.value)}>
            <option>request_sent</option>
            <option>missing_items</option>
            <option>received</option>
          </select>
          <button
            disabled={submission.state !== 'escalated'}
            onClick={() =>
              act('user-transitions', {
                event: 'human_resolved',
                resume_state: resumeState,
                note: 'resolved from harness UI',
              })
            }
          >
            Resolve
          </button>
          <button
            className="danger"
            disabled={['processed', 'cancelled'].includes(submission.state)}
            onClick={() => act('user-transitions', { event: 'waived', note: 'waived from harness UI' })}
          >
            Waive
          </button>
        </div>

        <button onClick={verifyAudit}>Verify audit chain</button>
        {audit && <span className={`pill ${audit.includes('OK') ? 'ok' : 'bad'}`}>{audit}</span>}
      </div>

      <h2>
        Transition log <span className="count">({transitions.length})</span>
      </h2>
      <table>
        <thead>
          <tr>
            <th>seq</th>
            <th>time</th>
            <th>edge</th>
            <th>event</th>
            <th>action</th>
            <th>actor</th>
            <th>payload</th>
          </tr>
        </thead>
        <tbody>
          {transitions.map((t) => (
            <tr key={t.seq}>
              <td className="mono">{t.seq}</td>
              <td className="mono">{timeOf(t.created_at)}</td>
              <td>
                <StateBadge state={t.from_state} /> → <StateBadge state={t.to_state} />
              </td>
              <td className="mono">{t.event}</td>
              <td className="mono">{t.action ?? '—'}</td>
              <td className="mono">{t.actor}</td>
              <td>
                {t.payload ? (
                  <details className="payload">
                    <summary>view</summary>
                    <pre>{JSON.stringify(t.payload, null, 2)}</pre>
                  </details>
                ) : (
                  '—'
                )}
              </td>
            </tr>
          ))}
          {transitions.length === 0 && (
            <tr>
              <td colSpan={7} className="note">
                No transitions yet — start the run, or have Manifest call Interface B.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="cheatsheet">
        <h2>Interface B cheatsheet (for Manifest engineers)</h2>
        <p className="note">
          Illegal edges return 409 with the current state — reconcile via GET, never retry the
          rejected write. Replaying the same idempotency_key returns the committed state as a no-op.
        </p>
        <pre>{curl}</pre>
      </div>
    </>
  );
}

// ——— Event feed ———

function Feed({ events }: { events: HarnessEvent[] }) {
  const arrow = { in: '⇦ in', out: '⇨ out', local: '· local' } as const;
  return (
    <>
      <h2>
        Seam traffic <span className="count">({events.length})</span>
      </h2>
      {events.map((e) => (
        <div
          key={e.id}
          className={`feed-item dir-${e.direction}${
            /reject|unauthor|invalid|fail/i.test(e.kind + e.summary) ? ' err' : ''
          }`}
        >
          <span className="when">{timeOf(e.at)}</span>{' '}
          <span className="kind">
            [{arrow[e.direction]}] {e.kind}
          </span>
          <div>{e.summary}</div>
          {e.detail != null && (
            <details className="payload">
              <summary>detail</summary>
              <pre>{JSON.stringify(e.detail, null, 2)}</pre>
            </details>
          )}
        </div>
      ))}
      {events.length === 0 && <p className="note">Nothing yet. Traffic in both directions lands here.</p>}
    </>
  );
}

// ——— App shell ———

function App() {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [events, setEvents] = useState<HarnessEvent[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [config, setConfig] = useState<HarnessConfig | null>(null);
  const [wsOk, setWsOk] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);

  const refresh = useCallback(async () => {
    const [subs, evts] = await Promise.all([
      api<{ submissions: Submission[] }>('/api/submissions'),
      api<{ events: HarnessEvent[] }>('/api/events'),
    ]);
    setSubmissions(subs.submissions);
    setEvents(evts.events);
  }, []);

  useEffect(() => {
    void refresh();
    void api<HarnessConfig>('/api/config').then(setConfig);

    let closed = false;
    const openWs = () => {
      const ws = new WebSocket(`${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`);
      wsRef.current = ws;
      ws.onopen = () => setWsOk(true);
      ws.onmessage = (msg) => {
        try {
          const data = JSON.parse(msg.data as string) as { type: string; event: HarnessEvent };
          if (data.type === 'event') {
            setEvents((prev) => [data.event, ...prev].slice(0, 300));
            void api<{ submissions: Submission[] }>('/api/submissions').then((r) =>
              setSubmissions(r.submissions),
            );
          }
        } catch {
          // ignore malformed frames
        }
      };
      ws.onclose = () => {
        setWsOk(false);
        if (!closed) setTimeout(openWs, 2000);
      };
    };
    openWs();
    return () => {
      closed = true;
      wsRef.current?.close();
    };
  }, [refresh]);

  const selected = submissions.find((s) => s.id === selectedId) ?? null;

  return (
    <div className="app">
      <header>
        <h1>Manifest Seam Harness</h1>
        <span className="sub">A: start run · B: audited write-back · C: signals · webhooks</span>
        <span className={`pill ${wsOk ? 'ok' : 'bad'}`}>{wsOk ? 'live' : 'reconnecting…'}</span>
        {config && (
          <span className={`pill ${config.manifest.configured ? 'ok' : 'warn'}`}>
            {config.manifest.configured
              ? `manifest: ${config.manifest.base_url}`
              : 'manifest: not configured'}
          </span>
        )}
        {config?.interface_b.token_is_default && (
          <span className="pill warn">Interface B token is the dev default</span>
        )}
      </header>
      <main>
        <div className="col">
          <h2>New submission</h2>
          <CreateForm onCreated={refresh} />
          <h2>
            Submissions <span className="count">({submissions.length})</span>
          </h2>
          {submissions.map((s) => (
            <div
              key={s.id}
              className={`card${s.id === selectedId ? ' selected' : ''}`}
              onClick={() => setSelectedId(s.id)}
            >
              <div className="title">
                {s.portco_name} <StateBadge state={s.state} />
              </div>
              <div className="meta">
                {s.tenant_id} · {s.period} · {s.received_items}/{s.expected_items} items
                {s.last_transition ? ` · last: ${s.last_transition.event}` : ''}
              </div>
            </div>
          ))}
          {submissions.length === 0 && <p className="note">No submissions yet.</p>}
        </div>
        <div className="col">
          {selected ? (
            <Detail submission={selected} config={config} refresh={() => void refresh()} />
          ) : (
            <p className="note">Select a submission to drive it through the seam.</p>
          )}
        </div>
        <div className="col">
          <Feed events={events} />
        </div>
      </main>
    </div>
  );
}

createRoot(document.getElementById('root')!).render(<App />);
